import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { apiGetTest, apiSearchQuestions } from 'core/api';
import usePromiseQuery from 'core/helpers/usePromiseQuery';
import { useStableCallback } from 'core/hooks/useStableCallback';
import { useMessageModal } from 'core/ui/MessageModal';

import { TSearchFilters } from '../Filters';
import useQuestionsApi from './useQuestionsApi';

type TRefState = {
  cursor?: string;
  filters: TSearchFilters;
  abort: null | (() => void);
};

type TState = {
  questions: TQuestion[];
  cursor: string | undefined;
  filters: TSearchFilters;
};

export default function useQuestionList() {
  const { showError } = useMessageModal();

  const [test, setTest] = useState<TTest | null>(null);
  const [questionsSt, setQuestionsSt] = useState<TState>({
    questions: [],
    cursor: undefined,
    filters: { text: '' },
  });

  const testUid = orDie(useParams<{ testUid?: string }>().testUid);

  const [doSearch, isFetching] = usePromiseQuery(apiSearchQuestions);

  const { current: questionsRef } = useRef<TRefState>({
    abort: null,
  } as TRefState);
  questionsRef.cursor = questionsSt.cursor;
  questionsRef.filters = questionsSt.filters;

  /**
   * Fetch from the scratch without pagination
   */
  const fetchInitial = useCallback(() => {
    questionsRef.abort?.();
    let isAborted = false;
    questionsRef.abort = () => { isAborted = true; }; // prettier-ignore

    // reset active state to show empty list and preloader
    setQuestionsSt(st => ({ ...st, questions: [], cursor: undefined }));

    return doSearch(testUid, undefined /* no cursor */, questionsRef.filters)
      .then(({ questions, cursor }) => {
        if (isAborted) return;

        questionsRef.cursor = cursor;
        questionsRef.abort = null;
        setQuestionsSt(st => ({ ...st, cursor, questions }));
      })
      .catch(showError);
  }, [testUid]);

  useEffect(() => {
    Promise.all([apiGetTest(testUid), fetchInitial()])
      .then(([test]) => {
        setTest(test);
      })
      .catch(showError);
  }, [testUid]);

  const refFetching = useRef(isFetching);
  refFetching.current = isFetching;

  /**
   * Subsequent fetches with a cursor
   */
  const fetchMore = useCallback(() => {
    const isFetching = refFetching.current;
    const { cursor, filters } = questionsRef;

    /*
     * it's important to get these values from the ref, because state values
     * may become obsolete before fetchMore is updated (dependencies won't
     * help with it, because fetchMore may be called multiple times after state
     * was changed but before any render happened)
     */
    if (!cursor || isFetching) return;

    questionsRef.abort?.();
    let isAborted = false;
    questionsRef.abort = () => { isAborted = true; }; // prettier-ignore

    doSearch(testUid, cursor, filters)
      .then(({ questions, cursor }) => {
        if (isAborted) return;

        questionsRef.cursor = cursor;
        questionsRef.abort = null;
        setQuestionsSt(st => ({
          ...st,
          questions: [...st.questions, ...questions],
          cursor,
        }));
      })
      .catch(showError);
  }, [testUid]);

  const questionsApi = useQuestionsApi(
    useStableCallback((fn: (prev: TQuestion[]) => TQuestion[]): void => {
      setQuestionsSt(st => ({
        ...st,
        questions: fn(st.questions),
      }));
    })
  );

  const setFilter = useStableCallback(
    <T extends keyof TSearchFilters>(
      key: T,
      value: TSearchFilters[T]
    ): void => {
      setQuestionsSt(st => ({
        ...st,
        filters: {
          ...st.filters,
          [key]: value,
        },
      }));
    }
  );

  const [showOptions, setShowOptions] = useState(false);
  const onToggleOptions = useStableCallback(() =>
    setShowOptions(show => !show)
  );

  return {
    fetchInitial,
    fetchMore,
    questions: questionsSt.questions,
    filters: questionsSt.filters,
    setFilter,
    isFetching,
    test: test,
    testUid,
    showOptions,
    onToggleOptions,
    ...questionsApi,
  };
}
